import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { incidentService } from "@iso/services";

/**
 * Whether the current user is allowed to assign-user / set-expiry / bulk-
 * delete SDS files for the active property.
 *
 * Mirrors the backend AttachmentPermissionService rule for property scope:
 *   - active permission slug is "owner" or "admin", OR
 *   - is_safety_admin on the active property (from incident settings).
 *
 * --------------------------------------------------------------------------
 *  Scope assumption (intentional, PR #358 review S1):
 * --------------------------------------------------------------------------
 *  SDS is single-property-scoped today — every property has exactly one
 *  is_system "Safety Data Sheets" folder, and the page only ever shows
 *  files belonging to the active property. As a result this hook has no
 *  corporate-group branch like useCanManageFiles does.
 *
 *  If SDS ever gains a corporate-library view, the is_safety_admin lookup
 *  here will need to fan out across every property in the corporate group
 *  (one getSettings round-trip per property) — extend the hook to accept a
 *  scope arg or split into useCanManageSDSProperty / -Corporate helpers
 *  rather than papering over with a single-property proxy.
 *
 *  Kept separate from useCanManageFiles so SDS can adjust the rule (e.g.
 *  stricter delete gating) independently of file manager.
 * --------------------------------------------------------------------------
 */
const useCanManageSDSFiles = () => {
  const [canManage, setCanManage] = useState(false);

  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activePermission = useSelector(
    (state) => state.permission?.activePermission
  );

  useEffect(() => {
    if (!activePropertyId) {
      setCanManage(false);
      return;
    }
    if (["owner", "admin"].includes(activePermission)) {
      setCanManage(true);
      return;
    }
    let cancelled = false;
    incidentService
      .getSettings(activePropertyId)
      .then((response) => {
        if (cancelled) return;
        setCanManage(Boolean(response?.is_safety_admin));
      })
      .catch(() => {
        if (!cancelled) setCanManage(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activePropertyId, activePermission]);

  return canManage;
};

export default useCanManageSDSFiles;
